import React, { useState, useEffect, useCallback } from 'react';
import {
    X, CheckSquare, Award, BookOpen, AlertTriangle,
    XCircle, Download, RefreshCw, BarChart3, TrendingUp,
    Users, AlertCircle, CheckCircle, ChevronRight, PenTool, Upload, FileCheck,
    Maximize2, Minimize2
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    BarChart,
    DataTable,
    LineChart,
    EmployeeDetailModal,
    ExpandableVisualization
} from '../Analytics';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import AnalyticsEmptyState from './AnalyticsEmptyState';
import NotificationSettingsPanel from './NotificationSettingsPanel';
import ActivityReminderSection from './ActivityReminderSection';

// Token styles matching functionality
const TOKENS = {
    textPrimary: '#202938',
    textSecondary: '#626B7A',
    border: '#E5E1DD',
    bgCard: '#FFFFFF',
    bgPage: '#FAFAF9',
    cardRadius: '6px',
};

const TasksAnalyticsDrawer = ({ isOpen, onClose }) => {
    const [data, setData] = useState(null);
    const [employeeData, setEmployeeData] = useState(null);
    const [trends, setTrends] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps, dragHandleElement, isFullscreen, toggleFullscreen } = useDrawerResize({
        minWidth: 400, maxWidth: 800, defaultWidth: 600,
        storageKey: 'tasksAnalyticsDrawerWidth',
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [tasksRes, trendsRes, employeesRes] = await Promise.all([
                api.get('/analytics/tasks', { headers }),
                api.get('/analytics/trends', { headers }),
                api.get('/analytics/employees', { headers })
            ]);

            setData(tasksRes.data.analytics);
            setTrends(trendsRes.data.trends);
            setEmployeeData(employeesRes.data);
        } catch (error) {
            console.error('Error fetching task analytics:', error);
            toast.error('Failed to load task analytics');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'tasks', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'task-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Task analytics exported');
        } catch { toast.error('Export failed'); }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
            document.body.style.overflow = 'hidden';
        } else {
            setData(null);
            setTrends(null);
            document.body.style.overflow = 'unset';
            setLoading(true);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, fetchData]);

    // Close on Escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end transform transition-transform duration-300">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                className="relative h-full bg-[#FAFAF9] shadow-2xl flex flex-col animate-slide-in-right"
                style={{
                    width: isFullscreen ? '100vw' : `${drawerWidth}px`,
                    maxWidth: '100vw',
                    transition: isDragging ? 'none' : 'width 0.3s ease',
                }}
            >
                {/* ── Drag handle (left edge) — hidden in fullscreen ── */}
                {!isFullscreen && (
                    <div {...dragHandleProps}>
                        <div {...dragHandleElement} />
                    </div>
                )}
                {/* Header */}
                <div className="px-6 py-4 bg-white border-b border-[#E5E1DD] flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-lg">
                            <BarChart3 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Task Analytics</h2>
                            <p className="text-sm text-gray-500">Performance metrics and insights</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleDownloadCSV}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Download CSV"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={fetchData}
                            disabled={loading}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refresh Data"
                        >
                            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 scrollbar-hide min-w-0">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-64 space-y-4">
                            <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                            <p className="text-gray-500 font-medium">Loading analytics...</p>
                        </div>
                    ) : data ? (
                        <div className="space-y-6">
                            {/* Task Type Breakdown */}
                            {data.byType && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {data.byType.map(type => (
                                        <StatCard
                                            key={type.taskType}
                                            title={type.taskType.charAt(0).toUpperCase() + type.taskType.slice(1)}
                                            value={type.taskCount}
                                            subtitle={`${type.completionRate}% completion`}
                                            icon={type.taskType === 'quiz' ? Award : type.taskType === 'training' ? BookOpen : CheckSquare}
                                            color={type.completionRate >= 80 ? 'blue' : type.completionRate >= 50 ? 'blue' : 'red'}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Trends Charts */}
                            {trends && (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Task Completions Trend */}
                                    {trends.taskCompletions && (
                                        <ExpandableVisualization
                                            title="Completion Activity"
                                            data={trends.taskCompletions.map(d => ({ date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), completions: d.completions }))}
                                            columns={[{ key: 'date', label: 'Date' }, { key: 'completions', label: 'Completions' }]}
                                            exportFilename="task-completions-trend"
                                        >
                                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <TrendingUp className="w-5 h-5 text-blue-600" />
                                                    <h3 className="font-semibold text-gray-800">Comp. Activity</h3>
                                                </div>
                                                <LineChart
                                                    data={trends.taskCompletions.map(d => ({
                                                        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                        value: d.completions
                                                    }))}
                                                    height={200}
                                                    lineColor="#3B82F6"
                                                />
                                            </div>
                                        </ExpandableVisualization>
                                    )}

                                    {/* Quiz Scores Trend */}
                                    {trends.quizScores && (
                                        <ExpandableVisualization
                                            title="Average Quiz Scores"
                                            data={trends.quizScores.map(d => ({ date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), avgScore: d.avgScore }))}
                                            columns={[{ key: 'date', label: 'Date' }, { key: 'avgScore', label: 'Avg Score' }]}
                                            exportFilename="quiz-scores-trend"
                                        >
                                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <Award className="w-5 h-5 text-blue-500" />
                                                    <h3 className="font-semibold text-gray-800">Avg Quiz Scores</h3>
                                                </div>
                                                <LineChart
                                                    data={trends.quizScores.map(d => ({
                                                        date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                                                        value: d.avgScore
                                                    }))}
                                                    height={200}
                                                    lineColor="#60A5FA"
                                                />
                                            </div>
                                        </ExpandableVisualization>
                                    )}
                                </div>
                            )}

                            {/* Quiz Analytics Table */}
                            {data.quizzes && data.quizzes.length > 0 && (
                                <ExpandableVisualization
                                    title="Quiz Performance"
                                    subtitle="Detailed quiz scores and pass rates"
                                    data={data.quizzes}
                                    columns={[
                                        { key: 'title', label: 'Quiz' },
                                        { key: 'totalAttempts', label: 'Attempts' },
                                        { key: 'avgScore', label: 'Avg Score' },
                                        { key: 'passRate', label: 'Pass Rate' },
                                        { key: 'avgAttempts', label: 'Avg Attempts' },
                                        { key: 'minScore', label: 'Min Score' },
                                        { key: 'maxScore', label: 'Max Score' },
                                    ]}
                                    exportFilename="quiz-performance"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] overflow-hidden shadow-sm">
                                        <div className="p-4 border-b border-[#E5E1DD]">
                                            <h3 className="font-semibold text-gray-800">Quiz Performance</h3>
                                            <p className="text-sm text-gray-500">Detailed quiz scores and pass rates</p>
                                        </div>
                                        <DataTable
                                            data={data.quizzes}
                                            columns={[
                                                { key: 'title', label: 'Quiz', render: (val) => <span className="font-medium text-gray-900 truncate max-w-xs block">{val}</span> },
                                                { key: 'totalAttempts', label: 'Attempts' },
                                                {
                                                    key: 'avgScore', label: 'Avg Score', render: (val, row) => (
                                                        <span className={val >= row.passingScore ? 'text-blue-600 font-medium' : 'text-red-600 font-medium'}>
                                                            {val}%
                                                        </span>
                                                    )
                                                },
                                                {
                                                    key: 'passRate', label: 'Pass Rate', render: (val) => (
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                                <div className={`h-full rounded-full ${val >= 70 ? 'bg-blue-500' : val >= 50 ? 'bg-blue-300' : 'bg-red-500'}`} style={{ width: `${val}%` }} />
                                                            </div>
                                                            <span>{val}%</span>
                                                        </div>
                                                    )
                                                },
                                                { key: 'avgAttempts', label: 'Avg Attempts' },
                                                { key: 'minScore', label: 'Min', render: (val) => `${val}%` },
                                                { key: 'maxScore', label: 'Max', render: (val) => `${val}%` }
                                            ]}
                                            searchable
                                            searchKeys={['title']}
                                            pageSize={5}
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* ── Signature & Proof Metrics ── */}
                            {(data.signatureMetrics?.totalAssignments > 0 || data.proofMetrics?.totalAssignments > 0) && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {data.signatureMetrics?.totalAssignments > 0 && (
                                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                            <div className="flex items-center gap-2 mb-3">
                                                <PenTool className="w-5 h-5 text-blue-600" />
                                                <h4 className="font-semibold text-gray-800">E-Signatures</h4>
                                            </div>
                                            <div className="text-3xl font-bold text-blue-600 mb-1">{data.signatureMetrics.completionRate}%</div>
                                            <p className="text-sm text-gray-500 mb-3">Completion rate</p>
                                            <div className="flex items-center gap-4 text-sm">
                                                <span className="text-blue-600 font-medium">✓ {data.signatureMetrics.signed} signed</span>
                                                <span className="text-blue-400 font-medium">⏳ {data.signatureMetrics.pending} pending</span>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-2">
                                                Avg time to sign: {data.signatureMetrics.avgHoursToSign}h
                                            </p>
                                        </div>
                                    )}
                                    {data.proofMetrics?.totalAssignments > 0 && (
                                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Upload className="w-5 h-5 text-blue-600" />
                                                <h4 className="font-semibold text-gray-800">Proof Uploads</h4>
                                            </div>
                                            <div className="text-3xl font-bold text-blue-600 mb-1">{data.proofMetrics.submissionRate}%</div>
                                            <p className="text-sm text-gray-500 mb-3">Submission rate</p>
                                            <div className="flex items-center gap-4 text-sm">
                                                <span className="text-blue-600 font-medium">✓ {data.proofMetrics.submitted} submitted</span>
                                                <span className="text-blue-400 font-medium">⏳ {data.proofMetrics.pending} pending</span>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-2">
                                                Avg days to submit: {data.proofMetrics.avgDaysToSubmit}d
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Score Distribution ── */}
                            {data.scoreDistribution && data.scoreDistribution.length > 0 && (
                                <ExpandableVisualization
                                    title="Score Distribution"
                                    data={data.scoreDistribution.map(d => ({ band: d.band, count: d.count }))}
                                    columns={[{ key: 'band', label: 'Score Band' }, { key: 'count', label: 'Count' }]}
                                    exportFilename="score-distribution"
                                >
                                    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                        <div className="flex items-center gap-2 mb-4">
                                            <Award className="w-5 h-5 text-blue-500" />
                                            <h3 className="font-semibold text-gray-800">Score Distribution</h3>
                                        </div>
                                        <BarChart
                                            data={data.scoreDistribution.map(d => ({
                                                label: d.band,
                                                value: d.count,
                                                color: d.band === '81-100' ? '#2563EB' : d.band === '61-80' ? '#3B82F6' : d.band === '41-60' ? '#60A5FA' : d.band === '21-40' ? '#93C5FD' : '#EF4444'
                                            }))}
                                            showValues
                                        />
                                        {data.attemptPassRates && (
                                            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                                                <div className="text-center">
                                                    <p className="text-xs text-gray-500 mb-1">1st Attempt Pass Rate</p>
                                                    <p className="text-xl font-bold text-blue-600">{data.attemptPassRates.firstAttemptPassRate}%</p>
                                                    <p className="text-xs text-gray-400">{data.attemptPassRates.firstAttemptPassed}/{data.attemptPassRates.firstAttemptTotal}</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-xs text-gray-500 mb-1">Retry Pass Rate</p>
                                                    <p className="text-xl font-bold text-blue-400">{data.attemptPassRates.retryPassRate}%</p>
                                                    <p className="text-xs text-gray-400">{data.attemptPassRates.retryPassed}/{data.attemptPassRates.retryTotal}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Retry Patterns */}
                            {data.retryPatterns && data.retryPatterns.length > 0 && (
                                <ExpandableVisualization
                                    title="Retry Patterns"
                                    subtitle="Tasks with multiple attempts"
                                    data={data.retryPatterns}
                                    columns={[{ key: 'title', label: 'Task' }, { key: 'avgAttempts', label: 'Avg Attempts' }, { key: 'totalRetries', label: 'Total Retries' }]}
                                    exportFilename="retry-patterns"
                                >
                                    <div className="bg-white rounded-xl border border-[#E5E1DD] p-6 shadow-sm">
                                        <h3 className="font-semibold text-gray-800 mb-4">Retry Patterns</h3>
                                        <p className="text-sm text-gray-500 mb-4">Tasks with multiple attempts</p>
                                        <BarChart
                                            data={data.retryPatterns.slice(0, 10).map(t => ({
                                                label: t.title.length > 20 ? t.title.substring(0, 20) + '...' : t.title,
                                                value: parseFloat(t.avgAttempts),
                                                color: parseFloat(t.avgAttempts) > 2 ? '#EF4444' : '#3B82F6'
                                            }))}
                                            horizontal
                                            showValues
                                        />
                                    </div>
                                </ExpandableVisualization>
                            )}

                            {/* Abandonment Rates */}
                            {data.abandonmentRates && data.abandonmentRates.length > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                                    <div className="flex items-start gap-3">
                                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-red-800">High Abandonment Tasks</h4>
                                            <p className="text-sm text-red-700 mb-4">
                                                Tasks that employees start but don't complete
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {data.abandonmentRates.filter(t => t.abandonmentRate > 10).slice(0, 6).map(task => (
                                                    <div key={task.id} className="bg-white/60 rounded-lg p-3">
                                                        <p className="font-medium text-gray-900 truncate">{task.title}</p>
                                                        <div className="flex items-center gap-4 mt-1 text-sm">
                                                            <span className="text-red-600 font-medium">{task.abandonmentRate}% abandoned</span>
                                                            <span className="text-gray-500">{task.likelyAbandoned} of {task.totalStarted}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <AnalyticsEmptyState
                            icon={BarChart3}
                            title="No Task Analytics Available"
                            message="We couldn't load task analytics at this time."
                            onRetry={fetchData}
                        />
                    )}

                    {/* Employee Performance Section */}
                    {employeeData && (
                        <div className="space-y-6 mt-8 pt-8 border-t border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900">Employee Performance</h3>

                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <StatCard
                                    title="Total Employees"
                                    value={employeeData.summary.total}
                                    icon={Users}
                                    color="blue"
                                />
                                <div className="bg-red-50 rounded-xl border border-red-200 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertTriangle className="w-5 h-5 text-red-500" />
                                        <span className="text-sm text-red-600">High Risk</span>
                                    </div>
                                    <p className="text-2xl font-bold text-red-700">{employeeData.summary.highRisk}</p>
                                </div>
                                <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertCircle className="w-5 h-5 text-blue-500" />
                                        <span className="text-sm text-blue-600">Medium Risk</span>
                                    </div>
                                    <p className="text-2xl font-bold text-blue-700">{employeeData.summary.mediumRisk}</p>
                                </div>
                                <div className="bg-blue-50 rounded-xl border border-blue-200 p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="w-5 h-5 text-blue-500" />
                                        <span className="text-sm text-blue-600">Low Risk</span>
                                    </div>
                                    <p className="text-2xl font-bold text-blue-700">{employeeData.summary.lowRisk}</p>
                                </div>
                            </div>

                            {/* Employee Table */}
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="p-4 border-b border-gray-200">
                                    <h3 className="font-semibold text-gray-800">Individual Progress</h3>
                                    <p className="text-sm text-gray-500">Click on an employee to view detailed stats</p>
                                </div>
                                <DataTable
                                    data={employeeData.employees}
                                    columns={[
                                        {
                                            key: 'name', label: 'Employee', render: (val, row) => (
                                                <div className="flex items-center gap-2">
                                                    <div>
                                                        <p className="font-medium text-gray-900">{val}</p>
                                                        <p className="text-xs text-gray-500">{row.position || row.email}</p>
                                                    </div>
                                                    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                                                </div>
                                            )
                                        },
                                        { key: 'department', label: 'Department' },
                                        {
                                            key: 'metrics.preboarding', label: 'Preboarding', render: (_, row) => {
                                                const data = row.metrics?.preboarding || { progress: 0, completed: 0, total: 0 };
                                                return (
                                                    <div className="flex flex-col gap-1 w-24">
                                                        <div className="flex justify-between text-xs text-gray-600">
                                                            <span>{data.progress}%</span>
                                                            <span>{data.completed}/{data.total}</span>
                                                        </div>
                                                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-blue-400 rounded-full" style={{ width: `${data.progress}%` }} />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            key: 'metrics.onboarding', label: 'Onboarding', render: (_, row) => {
                                                const data = row.metrics?.onboarding || { progress: 0, completed: 0, total: 0 };
                                                return (
                                                    <div className="flex flex-col gap-1 w-24">
                                                        <div className="flex justify-between text-xs text-gray-600">
                                                            <span>{data.progress}%</span>
                                                            <span>{data.completed}/{data.total}</span>
                                                        </div>
                                                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${data.progress}%` }} />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            key: 'metrics', label: 'Overdue', render: (val) => (
                                                <span className={val.overdueTasks > 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}>
                                                    {val.overdueTasks}
                                                </span>
                                            )
                                        },
                                        {
                                            key: 'engagementScore', label: 'Engagement', render: (val) => (
                                                <span className={`font-medium ${val >= 70 ? 'text-blue-600' : val >= 40 ? 'text-blue-400' : 'text-red-600'}`}>
                                                    {val}%
                                                </span>
                                            )
                                        },
                                        {
                                            key: 'riskFactors', label: 'Risk Factors', render: (val) => (
                                                <div className="flex flex-wrap gap-1">
                                                    {val.slice(0, 2).map((factor, i) => (
                                                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                                            {factor}
                                                        </span>
                                                    ))}
                                                    {val.length > 2 && (
                                                        <span className="text-xs text-gray-400">+{val.length - 2}</span>
                                                    )}
                                                </div>
                                            )
                                        }
                                    ]}
                                    searchable
                                    searchKeys={['name', 'email', 'department', 'position']}
                                    pageSize={10}
                                    onRowClick={(row) => setSelectedEmployeeId(row.id)}
                                />
                            </div>
                        </div>
                    )}

                    {/* AI Chat Panel */}
                    <AnalyticsAIPanel
                        analyticsContext={{
                            type: 'tasks',
                            title: 'Task Analytics',
                            data: data,
                        }}
                    />

                    {/* Activity Reminder Section */}
                    <ActivityReminderSection
                        activityType="task"
                        activityId={data?.byType?.[0]?.taskId}
                        activityTitle="Tasks"
                    />

                    {/* Notification Settings Panel */}
                    <NotificationSettingsPanel
                        analyticsContext={{
                            type: 'tasks',
                            title: 'Task Analytics',
                            data: data,
                        }}
                    />
                </div>

                {/* Employee Detail Modal */}
                {selectedEmployeeId && (
                    <EmployeeDetailModal
                        employeeId={selectedEmployeeId}
                        onClose={() => setSelectedEmployeeId(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default TasksAnalyticsDrawer;
